import { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { useAppSelector } from 'hooks';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Checkbox } from 'components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from 'components/ui/dialog';
import { activityRequest } from './contracts';

export default function AddActivityDialog({
  onClose,
  prefill,
}: {
  onClose: () => void;
  prefill: { deviceId: string; title: string };
}) {
  const { orgId } = useParams<{ orgId: string }>();
  const history = useHistory();
  const client = useQueryClient();
  const [title, setTitle] = useState(prefill.title);
  const [deviceId, setDeviceId] = useState(prefill.deviceId);
  const [responsibles, setResponsibles] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const members = useAppSelector(
    (state) =>
      state.orgs.orgMap?.[orgId]?.members ||
      (state.orgs.currentOrg?.id === orgId ? state.orgs.currentOrg.members : {})
  );
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const result = await activityRequest(
        'SETUP_MACHINE_ACTIVITY',
        { orgId, deviceId: deviceId.trim(), title, internalResponsibles: responsibles },
        z.object({ orgId: z.string(), sensorId: z.string() })
      );
      await client.invalidateQueries({ queryKey: ['inventory-sensor-register'] });
      onClose();
      history.push(`/orgs/${result.orgId}/sensors/${result.sensorId}/machine-activity`);
    } catch (failure) {
      setError(
        failure instanceof Error ? failure.message : 'Sensoren kunne ikke oprettes. Prøv igen.'
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Tilføj maskinaktivitet</DialogTitle>
          <DialogDescription>
            Registrér boksens ID hos kunden. Sensoren starter i Test uden driftsbeskeder.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <label className="block space-y-1">
            Sensorens navn
            <Input
              required
              maxLength={120}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              disabled={busy}
            />
          </label>
          <label className="block space-y-1">
            Boks-ID
            <Input
              required
              pattern="[a-zA-Z0-9_-]{1,160}"
              value={deviceId}
              onChange={(event) => setDeviceId(event.target.value)}
              disabled={busy}
            />
          </label>
          <fieldset disabled={busy} className="space-y-2">
            <legend>Ansvarlige for beskeder</legend>
            {Object.values(members || {})
              .filter((member) => member.email)
              .map((member) => (
                <label className="flex items-center gap-2" key={member.email}>
                  <Checkbox
                    disabled={busy}
                    checked={responsibles.includes(member.email)}
                    onCheckedChange={(checked) =>
                      setResponsibles(
                        checked === true
                          ? [...responsibles, member.email]
                          : responsibles.filter((email) => email !== member.email)
                      )
                    }
                  />
                  {member.name || member.email}
                </label>
              ))}
            <p className="text-sm text-muted-foreground">
              Kundens eksisterende abonnementsvalg bruges også. Alarmgrænser og kanaler vælges under
              Indstillinger.
            </p>
          </fieldset>
          {error && (
            <p role="alert" className="text-red-700">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" type="button" disabled={busy} onClick={onClose}>
              Annuller
            </Button>
            <Button disabled={busy || !title.trim() || !deviceId.trim()} type="submit">
              {busy ? 'Opretter…' : 'Opret i Test'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
